/**
 * Command registry (SPEC §17.5): every action HELM can perform is registered here so the palette,
 * hotkeys and menus all dispatch through one place. Trading actions still run their
 * confirmations; the registry never bypasses `OrderRouter`.
 */

export type CommandGroup = "trading" | "workspace" | "chart" | "settings" | "navigation" | "session";

export type CommandContext = {
  /** Where the command was invoked from, for audit/telemetry. */
  source: "palette" | "hotkey" | "menu" | "test";
};

export type Command = {
  id: string;
  title: string;
  group: CommandGroup;
  keywords?: string[];
  /** Display hint only; binding lives in the hotkeys map. */
  hotkey?: string;
  /** Hidden from the palette when it returns false. */
  when?: () => boolean;
  run: (ctx: CommandContext) => void | Promise<void>;
};

export class CommandRegistry {
  private readonly commands = new Map<string, Command>();
  private readonly recent: string[] = [];

  register(cmd: Command): () => void {
    if (this.commands.has(cmd.id)) throw new Error(`command already registered: ${cmd.id}`);
    this.commands.set(cmd.id, cmd);
    return () => {
      this.commands.delete(cmd.id);
    };
  }

  get(id: string): Command | undefined {
    return this.commands.get(id);
  }

  /** Commands currently available (respecting `when`). */
  available(): Command[] {
    return [...this.commands.values()].filter((c) => c.when?.() ?? true);
  }

  /**
   * Fuzzy search: every query character must appear in order in `title` or a keyword
   * (case-insensitive). Recent commands rank first on an empty query; otherwise tighter matches
   * (fewer gaps, earlier start) rank higher.
   */
  search(query: string, limit = 12): Command[] {
    const q = query.trim().toLowerCase();
    const avail = this.available();
    if (q === "") {
      const recentSet = new Set(this.recent);
      return [
        ...this.recent
          .map((id) => this.commands.get(id))
          .filter((c): c is Command => c !== undefined && (c.when?.() ?? true)),
        ...avail.filter((c) => !recentSet.has(c.id)),
      ].slice(0, limit);
    }
    const scored = avail
      .map((c) => ({
        c,
        score: Math.min(...[c.title, ...(c.keywords ?? [])].map((t) => fuzzyScore(q, t.toLowerCase()))),
      }))
      .filter((s) => Number.isFinite(s.score))
      .sort((a, b) => a.score - b.score || a.c.title.localeCompare(b.c.title));
    return scored.slice(0, limit).map((s) => s.c);
  }

  async run(id: string, ctx: CommandContext): Promise<boolean> {
    const cmd = this.commands.get(id);
    if (!cmd || !(cmd.when?.() ?? true)) return false;
    this.recent.splice(0, this.recent.length, id, ...this.recent.filter((r) => r !== id).slice(0, 7));
    await cmd.run(ctx);
    return true;
  }
}

/** Lower is better; Infinity when the query is not a subsequence of the target. */
export function fuzzyScore(query: string, target: string): number {
  let ti = 0;
  let gaps = 0;
  let start = -1;
  for (const ch of query) {
    const idx = target.indexOf(ch, ti);
    if (idx === -1) return Infinity;
    if (start === -1) start = idx;
    else gaps += idx - ti;
    ti = idx + 1;
  }
  // Whole-word prefix matches beat scattered letters; earlier starts beat later ones.
  return gaps * 10 + start + (target.startsWith(query) ? -50 : 0);
}
